// src/components/pages/company/expenses/Expenses.tsx
import { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  Plus,
  Eye,
  Download,
  Trash2,
  Pencil,
  Copy,
  Columns,
  FileText,
  FileSpreadsheet,
  Receipt,
  SearchX,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import { MESES } from '@/components/constants/meses';
import {
  useGastosPorEmpresa,
  useExpenses,
} from '@/components/hooks/useExpenses';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import { CreateExpenseDialog } from '@/components/pages/dialogs/CreateExpenseDialog';

import {
  exportExpensesToExcelDynamic,
  exportExpensesToPDFDynamic,
  exportExpenseReceiptsToZip,
  ExpenseColumn,
} from '@/lib/exportExpenses';
import { toast } from 'sonner';
import Joyride from 'react-joyride';
import ExpensesTourSteps from '@/components/tour/company/ExpensesTourSteps';
import { useTranslation } from 'react-i18next';

import {
  DndContext,
  useDraggable,
  useDroppable,
  DragOverlay,
} from '@dnd-kit/core';
import { getTourStyles } from '@/components/tour/tourStyles';

import ExpenseCopyDialog from '@/components/pages/dialogs/ExpenseCopyDialog';
import ExpenseDeleteDialog from '@/components/pages/dialogs/ExpenseDeleteDialog';

import ColumnsSelectorDialog from '@/components/pages/dialogs/ColumnsSelectorDialog';
import Pagination from '@/components/pages/shared/Pagination';
import {
  DEFAULT_EXPENSE_COLUMNS,
  type ExpenseUiColumn,
  type ExpenseColumnId,
} from '@/components/types/expenseColumns';

const LS_COLUMNS_KEY = (idEmpresa: string) =>
  `expenses_columns_${idEmpresa || 'global'}`;

const DEFAULT_EXPENSES_PAGE_SIZE = 10;
const EXPENSES_PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const LS_PAGE_SIZE_KEY = (idEmpresa: string, seccion: 'fijos' | 'variables') =>
  `expenses_pageSize_${seccion}_${idEmpresa || 'global'}`;

const readStoredExpensesPageSize = (
  idEmpresa: string,
  seccion: 'fijos' | 'variables'
) => {
  if (typeof window === 'undefined') return DEFAULT_EXPENSES_PAGE_SIZE;
  const stored = Number(localStorage.getItem(LS_PAGE_SIZE_KEY(idEmpresa, seccion)));
  return EXPENSES_PAGE_SIZE_OPTIONS.includes(stored)
    ? stored
    : DEFAULT_EXPENSES_PAGE_SIZE;
};

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const rowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
};

type SortKey = 'concepto' | 'categoria' | 'fecha' | 'base' | 'iva' | 'total';
type SortDir = 'asc' | 'desc';

/* ===========================
   FIJAR ANCHOS PARA ALINEAR
=========================== */
const DRAG_COL_W = 44;
const ACTIONS_COL_W = 160;

const COL_W: Record<ExpenseColumnId, number> = {
  concepto: 320,
  categoria: 200,
  fecha: 140,
  base: 120,
  iva: 90,
  total: 140,
};

export default function Expenses() {
  const { t } = useTranslation();

  const empresaId = localStorage.getItem('id_empresa') || '';
  const now = new Date();

  const [selectedMonth, setSelectedMonth] = useState<number | null>(
    now.getMonth() + 1
  );
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedCategoria, setSelectedCategoria] = useState<string>('');
  const [filter, setFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState<any>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const isMobile = window.innerWidth < 640;
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const [openCopyDialog, setOpenCopyDialog] = useState(false);
  const [gastoToCopy, setGastoToCopy] = useState<any>(null);
  const [copyDate, setCopyDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  const [copyBase, setCopyBase] = useState<string>('');
  const [copyIva, setCopyIva] = useState<string>('');

  const [runTour, setRunTour] = useState(false);
  const [activeGasto, setActiveGasto] = useState<any>(null);

  // El botón global de ayuda (arriba a la derecha) lanza el tour de esta página.
  usePageHelp(() => setRunTour(true));
  useTourVisibility(runTour);

  const { gastos, loading, refetch } = useGastosPorEmpresa(
    empresaId,
    selectedMonth,
    selectedYear
  );
  const {
    eliminarGasto,
    copiarGasto,
    cambiarTipoGasto,
    crearGasto,
    editarGasto,
    tiposGasto,
    categoriasGasto,
  } = useExpenses(empresaId);

  const [columns, setColumns] = useState<ExpenseUiColumn[]>(() => {
    if (typeof window === 'undefined') return DEFAULT_EXPENSE_COLUMNS;

    try {
      const stored = localStorage.getItem(LS_COLUMNS_KEY(empresaId));
      if (!stored) return DEFAULT_EXPENSE_COLUMNS;

      const parsed = JSON.parse(stored) as {
        id: ExpenseColumnId;
        enabled: boolean;
      }[];

      return DEFAULT_EXPENSE_COLUMNS.map((defaultCol) => {
        const saved = parsed.find((c) => c.id === defaultCol.id);
        return saved ? { ...defaultCol, enabled: saved.enabled } : defaultCol;
      });
    } catch {
      return DEFAULT_EXPENSE_COLUMNS;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const minimal = columns.map(({ id, enabled }) => ({ id, enabled }));
    localStorage.setItem(LS_COLUMNS_KEY(empresaId), JSON.stringify(minimal));
  }, [columns, empresaId]);

  const resetColumns = () => {
    setColumns(DEFAULT_EXPENSE_COLUMNS);
    localStorage.removeItem(LS_COLUMNS_KEY(empresaId));
  };

  const [showColumnsDialog, setShowColumnsDialog] = useState(false);

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.enabled),
    [columns]
  );

  const filtered = useMemo(() => {
    const q = filter.toLowerCase().trim();
    if (!q && !selectedCategoria) return gastos || [];

    return (gastos || []).filter((g: any) => {
      const matchQuery =
        !q ||
        (g.concepto || '').toLowerCase().includes(q) ||
        (g.categoria_gasto || '').toLowerCase().includes(q);

      const matchCat =
        !selectedCategoria || g.categoria_gasto === selectedCategoria;
      return matchQuery && matchCat;
    });
  }, [gastos, filter, selectedCategoria]);

  const categoriasUnicas = useMemo(() => {
    const set = new Set<string>();
    (gastos || []).forEach((g: any) => {
      if (g.categoria_gasto) set.add(g.categoria_gasto);
    });
    return Array.from(set).sort();
  }, [gastos]);

  const getBase = (g: any) =>
    Number(g.base_imponible ?? g.recibos?.[0]?.base ?? 0);
  const getIva = (g: any) => Number(g.iva_porcentaje ?? 0);

  const getFechaValue = (g: any) => {
    const s = String(g.fecha_inicio ?? '').trim();
    if (!s) return 0;

    const ts = Date.parse(s);
    if (!Number.isNaN(ts)) return ts;

    const isoSpace = s.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (isoSpace) {
      const yyyy = Number(isoSpace[1]);
      const mm = Number(isoSpace[2]) - 1;
      const dd = Number(isoSpace[3]);
      const hh = Number(isoSpace[4] ?? 0);
      const mi = Number(isoSpace[5] ?? 0);
      const ss = Number(isoSpace[6] ?? 0);
      return new Date(yyyy, mm, dd, hh, mi, ss).getTime();
    }

    const dmy = s.match(
      /^(\d{2})[\/-](\d{2})[\/-](\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (dmy) {
      const dd = Number(dmy[1]);
      const mm = Number(dmy[2]) - 1;
      const yyyy = Number(dmy[3]);
      const hh = Number(dmy[4] ?? 0);
      const mi = Number(dmy[5] ?? 0);
      const ss = Number(dmy[6] ?? 0);
      return new Date(yyyy, mm, dd, hh, mi, ss).getTime();
    }

    return 0;
  };

  const sortedFiltered = useMemo(() => {
    const arr = [...(filtered || [])];
    const dir = sortDir === 'asc' ? 1 : -1;

    const cmp = (a: any, b: any) => {
      if (!sortKey) return 0;

      let A: any;
      let B: any;

      switch (sortKey) {
        case 'concepto':
          A = (a.concepto ?? '').toString().toLowerCase();
          B = (b.concepto ?? '').toString().toLowerCase();
          break;
        case 'categoria':
          A = (a.categoria_gasto ?? '').toString().toLowerCase();
          B = (b.categoria_gasto ?? '').toString().toLowerCase();
          break;
        case 'fecha':
          A = getFechaValue(a);
          B = getFechaValue(b);
          break;
        case 'base':
          A = getBase(a);
          B = getBase(b);
          break;
        case 'iva':
          A = getIva(a);
          B = getIva(b);
          break;
        case 'total':
          A = Number(a.total_mes ?? 0);
          B = Number(b.total_mes ?? 0);
          break;
        default:
          A = 0;
          B = 0;
      }

      if (A < B) return -1 * dir;
      if (A > B) return 1 * dir;
      return 0;
    };

    arr.sort(cmp);
    return arr;
  }, [filtered, sortKey, sortDir]);

  const gastosFijos = sortedFiltered.filter(
    (g: any) => g.tipo_gasto === 'fijo'
  );
  const gastosVariables = sortedFiltered.filter(
    (g: any) => g.tipo_gasto === 'variable'
  );

  /* ── Paginación (independiente para fijos y variables) ─────────── */
  const [fijosPage, setFijosPage] = useState(1);
  const [fijosPageSize, setFijosPageSize] = useState(() =>
    readStoredExpensesPageSize(empresaId, 'fijos')
  );
  const [variablesPage, setVariablesPage] = useState(1);
  const [variablesPageSize, setVariablesPageSize] = useState(() =>
    readStoredExpensesPageSize(empresaId, 'variables')
  );

  const fijosTotalPages = Math.max(
    1,
    Math.ceil(gastosFijos.length / fijosPageSize)
  );
  const variablesTotalPages = Math.max(
    1,
    Math.ceil(gastosVariables.length / variablesPageSize)
  );

  useEffect(() => {
    if (fijosPage > fijosTotalPages) setFijosPage(fijosTotalPages);
  }, [fijosPage, fijosTotalPages]);

  useEffect(() => {
    if (variablesPage > variablesTotalPages)
      setVariablesPage(variablesTotalPages);
  }, [variablesPage, variablesTotalPages]);

  // Vuelve a la página 1 al cambiar filtro/mes/año/categoría, ya que el
  // contenido de ambas listas puede cambiar por completo.
  useEffect(() => {
    setFijosPage(1);
    setVariablesPage(1);
  }, [filter, selectedCategoria, selectedMonth, selectedYear]);

  const handleFijosPageSizeChange = (size: number) => {
    setFijosPageSize(size);
    setFijosPage(1);
    if (typeof window !== 'undefined') {
      localStorage.setItem(LS_PAGE_SIZE_KEY(empresaId, 'fijos'), String(size));
    }
  };

  const handleVariablesPageSizeChange = (size: number) => {
    setVariablesPageSize(size);
    setVariablesPage(1);
    if (typeof window !== 'undefined') {
      localStorage.setItem(
        LS_PAGE_SIZE_KEY(empresaId, 'variables'),
        String(size)
      );
    }
  };

  const gastosFijosPaginados = gastosFijos.slice(
    (fijosPage - 1) * fijosPageSize,
    fijosPage * fijosPageSize
  );
  const gastosVariablesPaginados = gastosVariables.slice(
    (variablesPage - 1) * variablesPageSize,
    variablesPage * variablesPageSize
  );

  const totalFijos = gastosFijos.reduce(
    (a, g) => a + Number(g.total_mes || 0),
    0
  );
  const totalVariables = gastosVariables.reduce(
    (a, g) => a + Number(g.total_mes || 0),
    0
  );

  const monthLabel = selectedMonth
    ? String(selectedMonth).padStart(2, '0')
    : 'todo';

  const expenseColumns: ExpenseColumn[] = useMemo(() => {
    return columns.map((c) => ({
      label: c.label,
      enabled: c.enabled,
      exportValue: c.exportValue,
    }));
  }, [columns]);

  const handleEliminar = async () => {
    if (!confirmDeleteId) return;
    await eliminarGasto(confirmDeleteId);
    refetch();
    setConfirmDeleteId(null);
  };

  const handleEdit = (gasto: any) => {
    setExpenseToEdit(gasto);
    setOpenDialog(true);
  };

  const handleOpenNewExpense = () => {
    setExpenseToEdit(null);
    setOpenDialog(true);
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const renderSortIcon = (key: SortKey) =>
    sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : null;

  const SortTh = ({
    label,
    k,
    className,
    alignRight,
  }: {
    label: string;
    k: SortKey;
    className?: string;
    alignRight?: boolean;
  }) => (
    <th
      onClick={() => toggleSort(k)}
      className={[
        'px-4 py-3 cursor-pointer bg-gray-100 dark:bg-white/5',
        'hover:bg-gray-200 dark:hover:bg-white/10 transition',
        alignRight ? 'text-right' : 'text-left',
        className ?? '',
      ].join(' ')}
      title={t('expenses.tooltipSort', { label })}
    >
      {label} {renderSortIcon(k)}
    </th>
  );

  /* -------------------------------------------------------
     ROW DRAGGABLE (solo desktop)
  ------------------------------------------------------- */
  const DraggableRow = ({ g, idx }: { g: any; idx: number }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } =
      useDraggable({
        id: g.id_gasto,
        data: { gasto: g },
      });

    const reciboUrl = g.recibos?.[0]?.url;
    const sinRecibo = !reciboUrl;

    const style: React.CSSProperties = {
      transform: transform
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : undefined,
      opacity: isDragging ? 0.6 : undefined,
    };

    return (
      <motion.tr
        ref={setNodeRef}
        style={style}
        variants={rowVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className={
          sinRecibo
            ? 'bg-amber-50 dark:bg-amber-900/20'
            : idx % 2 === 0
            ? 'bg-white dark:bg-transparent'
            : 'bg-gray-50 dark:bg-white/[0.03]'
        }
      >
        <td
          className="px-2 py-3 cursor-grab active:cursor-grabbing text-gray-400"
          style={{ width: DRAG_COL_W }}
          {...listeners}
          {...attributes}
        >
          ⠿
        </td>

        {visibleColumns.map((col) => {
          const isConcepto = col.id === 'concepto';

          return (
            <td
              key={col.id}
              className={[
                'px-4 py-3',
                col.alignRight ? 'text-right tabular-nums' : '',
                isConcepto
                  ? 'font-medium text-gray-800 dark:text-white'
                  : 'text-gray-600 dark:text-gray-400',
              ].join(' ')}
            >
              {isConcepto ? (
                <>
                  {col.render(g)}
                  {sinRecibo && (
                    <span className="tour-missing-receipt ml-2 text-xs text-amber-600">
                      {t('expenses.missingReceipt')}
                    </span>
                  )}
                </>
              ) : col.id === 'total' ? (
                <span className="font-semibold text-[#103254] dark:text-blue-400">
                  {col.render(g)}
                </span>
              ) : (
                col.render(g)
              )}
            </td>
          );
        })}

        <td
          className="px-4 py-3 text-right whitespace-nowrap"
          style={{ width: ACTIONS_COL_W }}
        >
          <div className="tour-row-actions flex items-center justify-end gap-2">
            {reciboUrl ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/10"
                  onClick={() => window.open(reciboUrl, '_blank')}
                  title={t('expenses.tooltipView')}
                >
                  <Eye className="h-4 w-4 text-[#103254] dark:text-blue-400" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/10"
                  title={t('expenses.tooltipDownload')}
                  onClick={async () => {
                    const res = await fetch(reciboUrl);
                    const blob = await res.blob();
                    const concepto = (g.concepto || 'recibo')
                      .trim()
                      .replace(/\s+/g, '_')
                      .replace(/[<>:"/\\|?*\x00-\x1F]/g, '');
                    const fecha = new Date(g.fecha_inicio);
                    const mes = String(fecha.getMonth() + 1).padStart(2, '0');
                    const año = fecha.getFullYear();
                    const ext =
                      blob.type === 'application/pdf'
                        ? 'pdf'
                        : blob.type.startsWith('image/')
                        ? 'jpg'
                        : 'file';
                    const filename = `${concepto}_${mes}_${año}.${ext}`;
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  }}
                >
                  <Download className="h-4 w-4 text-[#103254] dark:text-blue-400" />
                </Button>
              </>
            ) : (
              <>
                <span className="h-8 w-8" />
                <span className="h-8 w-8" />
              </>
            )}

            <Button
              className="tour-copy-action h-8 w-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/10"
              variant="ghost"
              size="icon"
              title={t('expenses.tooltipCopy')}
              onClick={() => {
                setGastoToCopy(g);
                setCopyDate(new Date().toISOString().split('T')[0]);
                setCopyBase(getBase(g).toFixed(2));
                setCopyIva(getIva(g).toFixed(2));
                setOpenCopyDialog(true);
              }}
            >
              <Copy className="h-4 w-4 text-[#103254] dark:text-blue-400" />
            </Button>

            <Button
              className="tour-edit-action h-8 w-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/10"
              variant="ghost"
              size="icon"
              onClick={() => handleEdit(g)}
              title={t('expenses.tooltipEdit')}
            >
              <Pencil className="h-4 w-4 text-[#103254] dark:text-blue-400" />
            </Button>

            <Button
              className="tour-delete-action h-8 w-8 flex items-center justify-center hover:bg-red-50 dark:hover:bg-red-900/20"
              variant="ghost"
              size="icon"
              onClick={() => setConfirmDeleteId(g.id_gasto)}
              title={t('expenses.tooltipDelete')}
            >
              <Trash2 className="h-4 w-4 text-red-500 dark:text-red-400" />
            </Button>
          </div>
        </td>
      </motion.tr>
    );
  };

  /* -------------------------------------------------------
     MOBILE CARD (una tarjeta por gasto)
  ------------------------------------------------------- */
  const MobileCard = ({ g }: { g: any }) => {
    const reciboUrl = g.recibos?.[0]?.url;
    const sinRecibo = !reciboUrl;

    return (
      <Card
        className={
          sinRecibo
            ? 'rounded-2xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20'
            : `rounded-2xl ${GLASS_CARD}`
        }
      >
        <CardContent className="p-4 space-y-2">
          {/* Título + total */}
          <div className="flex items-start justify-between gap-2">
            <span className="font-semibold text-gray-800 dark:text-white text-sm">
              {g.concepto}
              {sinRecibo && (
                <span className="ml-2 text-xs text-amber-600">
                  {t('expenses.missingReceipt')}
                </span>
              )}
            </span>
            <span className="font-bold text-[#103254] dark:text-blue-400 text-sm whitespace-nowrap">
              {Number(g.total_mes || 0).toFixed(2)} €
            </span>
          </div>

          {/* Categoría y fecha */}
          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            {g.categoria_gasto && <span>{g.categoria_gasto}</span>}
            {g.fecha_inicio && (
              <span>
                {new Date(g.fecha_inicio).toLocaleDateString('es-ES')}
              </span>
            )}
          </div>

          {/* Base e IVA */}
          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span>Base: {getBase(g).toFixed(2)} €</span>
            <span>IVA: {getIva(g)}%</span>
          </div>

          {/* Acciones */}
          <div className="flex items-center justify-end gap-1 pt-1 border-t border-gray-100 dark:border-white/10">
            {reciboUrl ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-white/10"
                  onClick={() => window.open(reciboUrl, '_blank')}
                  title={t('expenses.tooltipView')}
                >
                  <Eye className="h-4 w-4 text-[#103254] dark:text-blue-400" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-white/10"
                  title={t('expenses.tooltipDownload')}
                  onClick={async () => {
                    const res = await fetch(reciboUrl);
                    const blob = await res.blob();
                    const concepto = (g.concepto || 'recibo')
                      .trim()
                      .replace(/\s+/g, '_')
                      .replace(/[<>:"/\\|?*\x00-\x1F]/g, '');
                    const fecha = new Date(g.fecha_inicio);
                    const mes = String(fecha.getMonth() + 1).padStart(2, '0');
                    const año = fecha.getFullYear();
                    const ext =
                      blob.type === 'application/pdf'
                        ? 'pdf'
                        : blob.type.startsWith('image/')
                        ? 'jpg'
                        : 'file';
                    const filename = `${concepto}_${mes}_${año}.${ext}`;
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  }}
                >
                  <Download className="h-4 w-4 text-[#103254] dark:text-blue-400" />
                </Button>
              </>
            ) : (
              <>
                <span className="h-8 w-8" />
                <span className="h-8 w-8" />
              </>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-white/10"
              title={t('expenses.tooltipCopy')}
              onClick={() => {
                setGastoToCopy(g);
                setCopyDate(new Date().toISOString().split('T')[0]);
                setCopyBase(getBase(g).toFixed(2));
                setCopyIva(getIva(g).toFixed(2));
                setOpenCopyDialog(true);
              }}
            >
              <Copy className="h-4 w-4 text-[#103254] dark:text-blue-400" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-white/10"
              onClick={() => handleEdit(g)}
              title={t('expenses.tooltipEdit')}
            >
              <Pencil className="h-4 w-4 text-[#103254] dark:text-blue-400" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-red-50 dark:hover:bg-red-900/20"
              onClick={() => setConfirmDeleteId(g.id_gasto)}
              title={t('expenses.tooltipDelete')}
            >
              <Trash2 className="h-4 w-4 text-red-500 dark:text-red-400" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  /* -------------------------------------------------------
     DROPPABLE TABLE (desktop) + mobile cards
  ------------------------------------------------------- */
  const DroppableTable = ({
    droppableId,
    data,
  }: {
    droppableId: 'fijos' | 'variables';
    data: any[];
  }) => {
    const { setNodeRef, isOver } = useDroppable({ id: droppableId });

    return (
      <div
        ref={setNodeRef}
        className={isOver ? 'ring-2 ring-[#103254] rounded-2xl' : ''}
      >
        {/* ── Vista móvil / tablet ── */}
        <div className="flex flex-col gap-3 xl:hidden">
          {data.map((g: any) => (
            <MobileCard key={g.id_gasto} g={g} />
          ))}
        </div>

        {/* ── Vista desktop ── */}
        <Card className={`hidden xl:block rounded-2xl ${GLASS_CARD}`}>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full min-w-full table-fixed text-sm">
              <colgroup>
                <col style={{ width: DRAG_COL_W }} />
                {visibleColumns.map((c) => {
                  if (c.id === 'concepto') return <col key={c.id} />;
                  return <col key={c.id} style={{ width: COL_W[c.id] }} />;
                })}
                <col style={{ width: ACTIONS_COL_W }} />
              </colgroup>

              <thead className="bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 uppercase text-xs sticky top-0 z-10">
                <tr>
                  <th
                    className="px-4 py-3 text-left whitespace-nowrap hover:bg-gray-200 dark:hover:bg-white/10 transition"
                    style={{ width: DRAG_COL_W }}
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setShowColumnsDialog(true)}
                      title={t('expenses.tooltipColumns')}
                      className="h-5 w-5 p-0 tour-columns-button"
                    >
                      <Columns className="w-4 h-4" />
                    </Button>
                  </th>

                  {visibleColumns.map((col) => {
                    if (col.id === 'concepto')
                      return (
                        <SortTh key={col.id} label={col.label} k="concepto" />
                      );
                    if (col.id === 'categoria')
                      return (
                        <SortTh key={col.id} label={col.label} k="categoria" />
                      );
                    if (col.id === 'fecha')
                      return (
                        <SortTh key={col.id} label={col.label} k="fecha" />
                      );
                    if (col.id === 'base')
                      return (
                        <SortTh
                          key={col.id}
                          label={col.label}
                          k="base"
                          alignRight
                        />
                      );
                    if (col.id === 'iva')
                      return (
                        <SortTh
                          key={col.id}
                          label={col.label}
                          k="iva"
                          alignRight
                        />
                      );
                    if (col.id === 'total')
                      return (
                        <SortTh
                          key={col.id}
                          label={col.label}
                          k="total"
                          alignRight
                        />
                      );
                    return (
                      <th
                        key={col.id}
                        className={[
                          'px-4 py-3 text-left',
                          col.alignRight ? 'text-right' : '',
                        ].join(' ')}
                      >
                        {col.label}
                      </th>
                    );
                  })}

                  <th
                    className="px-4 py-3 text-right"
                    style={{ width: ACTIONS_COL_W }}
                  >
                    {t('expenses.colActions')}
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                {data.map((g: any, idx: number) => (
                  <DraggableRow key={g.id_gasto} g={g} idx={idx} />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    );
  };

  const handleDragEnd = async (event: any) => {
    const { over, active } = event;
    if (!over) return;

    const destino = over.id as 'fijos' | 'variables';
    const nuevoTipo = destino === 'fijos' ? 'fijo' : 'variable';

    const gasto = active?.data?.current?.gasto;
    const tipoActual = gasto?.tipo_gasto;

    if (tipoActual === nuevoTipo) return;

    await cambiarTipoGasto(active.id, nuevoTipo);
    refetch();
  };

  /* -------------------------------------------------------
     RENDER
  ------------------------------------------------------- */
  return (
    <DndContext
      onDragStart={(e) => setActiveGasto(e.active.data.current?.gasto)}
      onDragEnd={(e) => {
        handleDragEnd(e);
        setActiveGasto(null);
      }}
      onDragCancel={() => setActiveGasto(null)}
    >
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="w-full space-y-6 pb-28 lg:pb-0"
      >
        {/* Header — el botón de ayuda vive ahora arriba a la derecha, fijo
            y global (HelpButton), lanzado desde usePageHelp. */}
        <motion.div variants={itemVariants} className="w-full">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 tour-title">
            <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              {t('expenses.title')}
            </span>
          </div>
        </motion.div>

        {/* Tour */}
        <Joyride
          steps={ExpensesTourSteps}
          run={runTour}
          continuous
          showSkipButton
          spotlightClicks
          locale={{
            back: t('expenses.tour.back'),
            close: t('expenses.tour.close'),
            last: t('expenses.tour.last'),
            next: t('expenses.tour.next'),
            skip: t('expenses.tour.skip'),
          }}
          callback={({ status }) => {
            if (status === 'finished' || status === 'skipped')
              setRunTour(false);
          }}
          styles={getTourStyles(isMobile)}
        />

        {/* Filtros + exportación */}
        <Card className={`rounded-2xl ${GLASS_CARD}`}>
          <CardContent className="p-4 sm:p-6 space-y-4">
            <div className="tour-filters flex flex-col sm:flex-row sm:items-center gap-3">
              <Select
                value={selectedMonth ? String(selectedMonth) : 'all'}
                onValueChange={(v) =>
                  setSelectedMonth(v === 'all' ? null : Number(v))
                }
              >
                <SelectTrigger className="h-10 text-sm w-full sm:w-40 dark:bg-white/5 dark:border-white/10 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  <SelectItem value="all">
                    {t('expenses.filters.allMonths')}
                  </SelectItem>
                  {MESES.map((m, i) => (
                    <SelectItem key={i} value={String(i + 1)}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={String(selectedYear)}
                onValueChange={(v) => setSelectedYear(Number(v))}
              >
                <SelectTrigger className="h-10 text-sm w-full sm:w-28 dark:bg-white/5 dark:border-white/10 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  {Array.from(
                    { length: 5 },
                    (_, i) => now.getFullYear() + 1 - i
                  ).map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={selectedCategoria || 'all'}
                onValueChange={(v) =>
                  setSelectedCategoria(v === 'all' ? '' : v)
                }
              >
                <SelectTrigger className="tour-category-filter h-10 text-sm w-full sm:w-48 dark:bg-white/5 dark:border-white/10 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  <SelectItem value="all">
                    {t('expenses.filters.allCategories')}
                  </SelectItem>
                  {categoriasUnicas.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="tour-exports flex flex-col sm:flex-row sm:items-center gap-2 w-full">
              <Button
                type="button"
                onClick={async () =>
                  await exportExpensesToExcelDynamic(
                    filtered,
                    expenseColumns,
                    `gastos_${selectedYear}_${monthLabel}.xlsx`
                  )
                }
                className="w-full sm:w-auto bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] text-white rounded-lg transition-colors"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {t('expenses.exports.excel')}
              </Button>

              <Button
                type="button"
                onClick={() =>
                  exportExpensesToPDFDynamic(
                    filtered,
                    expenseColumns,
                    `gastos_${selectedYear}_${monthLabel}.pdf`
                  )
                }
                className="w-full sm:w-auto bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] text-white rounded-lg transition-colors"
              >
                <FileText className="w-4 h-4 mr-2" />
                {t('expenses.exports.pdf')}
              </Button>

              <Button
                type="button"
                onClick={async () => {
                  const res = await exportExpenseReceiptsToZip(
                    gastos,
                    `recibos_${selectedYear}_${monthLabel}.zip`
                  );

                  if (!res.ok) {
                    toast.error(t('expenses.exports.errorNoReceipts'));
                    return;
                  }

                  toast.success(
                    t('expenses.exports.successZip', { count: res.added })
                  );
                }}
                className="w-full sm:w-auto bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] text-white rounded-lg transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                {t('expenses.exports.zip')}
              </Button>
            </div>

            <div className="tour-total-month text-sm font-semibold text-gray-700 dark:text-gray-300 text-left sm:text-right w-full pt-2 border-t border-gray-100 dark:border-white/10">
              {t('expenses.totalMonth', {
                total: (totalFijos + totalVariables).toFixed(2),
                fijos: totalFijos.toFixed(2),
                variables: totalVariables.toFixed(2),
              })}
            </div>
          </CardContent>
        </Card>

        {/* Búsqueda + botón añadir */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row gap-3 justify-between items-center"
        >
          <Input
            placeholder={t('expenses.searchPlaceholder')}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="tour-search w-full sm:w-1/2 dark:bg-white/5 dark:border-white/10 dark:text-white"
          />

          {(gastos || []).length > 0 && (
            <Button
              onClick={handleOpenNewExpense}
              className="tour-add-expense w-full sm:w-auto bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] text-white rounded-lg font-medium shadow-md px-6 py-2 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('expenses.addBtn')}
            </Button>
          )}
        </motion.div>

        {/* Tablas / tarjetas */}
        {!loading && (
          <>
            {(gastos || []).length === 0 ? (
              <Card className={`rounded-2xl ${GLASS_CARD}`}>
                <CardContent className="py-16 text-center">
                  <Receipt className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                  <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                    {t('expenses.emptyTitle', 'Sin gastos registrados')}
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">
                    {t(
                      'expenses.emptySubtitle',
                      'Todavía no has añadido ningún gasto para este periodo.'
                    )}
                  </p>
                  <Button
                    onClick={handleOpenNewExpense}
                    className="rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] px-6 py-2 transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('expenses.addBtn')}
                  </Button>
                </CardContent>
              </Card>
            ) : sortedFiltered.length === 0 ? (
              <Card className={`rounded-2xl ${GLASS_CARD}`}>
                <CardContent className="py-16 text-center">
                  <SearchX className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                  <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                    {t('expenses.noMatchesTitle', 'Sin coincidencias')}
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    {t(
                      'expenses.noMatchesSubtitle',
                      'No se encontraron gastos con ese criterio.'
                    )}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {gastosFijos.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-base font-semibold text-[#103254] dark:text-blue-400 border-l-4 border-[#103254] pl-3">
                      {t('expenses.fixedTitle')}
                    </h3>
                    <DroppableTable
                      droppableId="fijos"
                      data={gastosFijosPaginados}
                    />
                    <Pagination
                      page={fijosPage}
                      totalPages={fijosTotalPages}
                      totalItems={gastosFijos.length}
                      pageSize={fijosPageSize}
                      onPageChange={setFijosPage}
                      onPageSizeChange={handleFijosPageSizeChange}
                      itemLabel="gastos fijos"
                      pageSizeOptions={EXPENSES_PAGE_SIZE_OPTIONS}
                    />
                  </div>
                )}

                {gastosVariables.length > 0 && (
                  <div className="space-y-4 pt-4">
                    <h3 className="text-base font-semibold text-[#103254] dark:text-blue-400 border-l-4 border-[#103254] pl-3">
                      {t('expenses.variableTitle')}
                    </h3>
                    <DroppableTable
                      droppableId="variables"
                      data={gastosVariablesPaginados}
                    />
                    <Pagination
                      page={variablesPage}
                      totalPages={variablesTotalPages}
                      totalItems={gastosVariables.length}
                      pageSize={variablesPageSize}
                      onPageChange={setVariablesPage}
                      onPageSizeChange={handleVariablesPageSizeChange}
                      itemLabel="gastos variables"
                      pageSizeOptions={EXPENSES_PAGE_SIZE_OPTIONS}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Dialogs */}
        <CreateExpenseDialog
          open={openDialog}
          onOpenChange={(open) => {
            setOpenDialog(open);
            if (!open) setExpenseToEdit(null);
          }}
          onSaved={refetch}
          expenseToEdit={expenseToEdit}
          tiposGasto={tiposGasto}
          categoriasGasto={categoriasGasto}
          crearGasto={crearGasto}
          editarGasto={editarGasto}
        />

        <ExpenseCopyDialog
          open={openCopyDialog}
          concepto={gastoToCopy?.concepto}
          copyDate={copyDate}
          setCopyDate={setCopyDate}
          copyBase={copyBase}
          setCopyBase={setCopyBase}
          copyIva={copyIva}
          setCopyIva={setCopyIva}
          onCancel={() => {
            setOpenCopyDialog(false);
            setGastoToCopy(null);
          }}
          onConfirm={async () => {
            if (!gastoToCopy) return;
            if (!copyDate) return;
            await copiarGasto(gastoToCopy.id_gasto, copyDate);
            setOpenCopyDialog(false);
            setGastoToCopy(null);
            refetch();
          }}
        />

        <ExpenseDeleteDialog
          open={!!confirmDeleteId}
          onClose={() => setConfirmDeleteId(null)}
          onConfirm={handleEliminar}
        />

        <ColumnsSelectorDialog
          open={showColumnsDialog}
          columns={columns}
          onChange={setColumns}
          onClose={() => setShowColumnsDialog(false)}
          onReset={resetColumns}
        />
      </motion.div>

      <DragOverlay>
        {activeGasto && (
          <div className="bg-white dark:bg-[#1c1c1e] dark:text-white shadow-xl rounded-lg px-4 py-2 text-sm border border-gray-200 dark:border-white/10">
            {activeGasto.concepto}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}